import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LayoutDashboard, FileText, Layers, FolderOpen, Building2, MapPin,
  Users, Mail, LogOut, Plus, Pencil, Trash2, Menu, X, Home, TrendingUp, BarChart3, PieChart, Activity,
  Instagram, Upload, ChevronDown, ChevronRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import MediaUpload from "@/components/MediaUpload";
import {
  getMe, logout as apiLogout, getStats,
  getSolutions, createSolution, updateSolution, deleteSolution,
  getSystems, createSystem, updateSystem, deleteSystem,
  getSheets, createSheet, updateSheet, deleteSheet,
  getProjects, createProject, updateProject, deleteProject,
  getDistributors, createDistributor, updateDistributor, deleteDistributor,
  getContacts, getLeads, getSettings, updateSetting, formatError, resolveMedia
} from "@/lib/api";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_20601db6-8b59-495a-899a-ce4625f47158/artifacts/u4f6xq9k_image.png";

function AnimatedCounter({ target, delay = 0 }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (target === 0) return;
    const timeout = setTimeout(() => {
      const duration = 800;
      const steps = 30;
      const increment = target / steps;
      let current = 0;
      const timer = setInterval(() => {
        current += increment;
        if (current >= target) { setCount(target); clearInterval(timer); }
        else setCount(Math.floor(current));
      }, duration / steps);
      return () => clearInterval(timer);
    }, delay);
    return () => clearTimeout(timeout);
  }, [target, delay]);
  return <div className="font-heading text-2xl text-zinc-900">{count}</div>;
}

const tabs = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "home", label: "Home", icon: Home },
  { id: "solutions", label: "Soluciones", icon: Layers },
  { id: "systems", label: "Sistemas", icon: FolderOpen },
  { id: "sheets", label: "Fichas Tecnicas", icon: FileText },
  { id: "projects", label: "Proyectos", icon: Building2 },
  { id: "distributors", label: "Distribuidores", icon: MapPin },
  { id: "contacts", label: "Contactos", icon: Mail },
  { id: "leads", label: "Leads", icon: Users },
  { id: "instagram", label: "Instagram", icon: Instagram },
];

function AdminTable({ items, columns, onEdit, onDelete }) {
  return (
    <div className="overflow-x-auto border border-zinc-200">
      <table className="w-full text-sm" data-testid="admin-table">
        <thead className="bg-zinc-50 border-b border-zinc-200">
          <tr>
            {columns.map(col => (
              <th key={col.key} className="text-left px-4 py-3 font-bold text-zinc-700 uppercase text-xs tracking-wider">{col.label}</th>
            ))}
            <th className="text-right px-4 py-3 font-bold text-zinc-700 uppercase text-xs tracking-wider">Acciones</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-100">
          {items.map((item, i) => (
            <tr key={item.id || i} className="hover:bg-zinc-50 transition-colors">
              {columns.map(col => (
                <td key={col.key} className="px-4 py-3 text-zinc-600 max-w-xs truncate">
                  {col.render ? col.render(item[col.key], item) : String(item[col.key] ?? "")}
                </td>
              ))}
              <td className="px-4 py-3 text-right">
                <div className="flex justify-end gap-2">
                  {onEdit && (
                    <button onClick={() => onEdit(item)} data-testid={`edit-btn-${i}`} className="p-1.5 text-zinc-400 hover:text-emerald-600 transition-colors">
                      <Pencil size={14} />
                    </button>
                  )}
                  {onDelete && (
                    <button onClick={() => onDelete(item)} data-testid={`delete-btn-${i}`} className="p-1.5 text-zinc-400 hover:text-red-500 transition-colors">
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {items.length === 0 && <div className="py-12 text-center text-zinc-400 text-sm">No hay registros</div>}
    </div>
  );
}


function HomeSettings() {
  const defaultHomeContent = {
    hero: {
      eyebrow: "Expertos en Impermeabilización",
      title: "Soluciones que protegen",
      highlight: "tu obra",
      subtitle:
        "Más de 15 años resolviendo problemas de filtraciones, humedad e impermeabilización en el sector industrial, comercial y residencial del Ecuador.",
      primaryLabel: "VER SOLUCIONES",
      primaryHref: "/soluciones",
      secondaryLabel: "SOLICITAR ASESORÍA",
      secondaryHref: "/contacto",
      images: [
        "/assets/construtop/home/hero-1.webp",
        "/assets/construtop/home/hero-2.webp",
        "/assets/construtop/home/hero-3.webp",
      ],
    },
    stats: {
      items: [
        { icon: "clock", value: "15+", label: "Años de experiencia" },
        { icon: "award", value: "1M+", label: "m² impermeabilizados" },
        { icon: "users", value: "500+", label: "Proyectos realizados" },
        { icon: "shield", value: "Instalación", label: "Garantizada" },
      ],
    },
    solutionsPreview: {
      eyebrow: "Soluciones por tipo de obra",
      title: "Encuentra la solución para tu proyecto",
      description:
        "Cada obra tiene desafíos únicos. Identificamos tu problema y aplicamos el sistema adecuado.",
      ctaLabel: "VER TODAS LAS SOLUCIONES",
      ctaHref: "/soluciones",
      visibleItems: 5,
    },
    systemsPreview: {
      eyebrow: "Sistemas de impermeabilización",
      title: "Tecnología de clase mundial",
      description:
        "Trabajamos con las marcas más reconocidas de Italia para ofrecer sistemas de impermeabilización de la más alta calidad.",
      ctaLabel: "EXPLORAR SISTEMAS",
      ctaHref: "/sistemas",
    },
    trust: {
      eyebrow: "Por qué Construtop",
      title: "Confianza técnica y respaldo profesional",
      items: [
        {
          icon: "shield",
          title: "Garantía real",
          desc: "Instalación\nGarantizada",
        },
        {
          icon: "award",
          title: "Aliados internacionales",
          desc: "Socios exclusivos de General Membrane, MPM y Metic de Italia.",
        },
        {
          icon: "users",
          title: "Equipo certificado",
          desc: "Ingenieros debidamente capacitados.",
        },
        {
          icon: "check",
          title: "Diagnóstico integral",
          desc: "Levantamiento de especificaciones y diseño de soluciones a medida.",
        },
      ],
    },
    cta: {
      title: "¿Tiene un problema de impermeabilización?",
      description:
        "Nuestro equipo de ingenieros especializados le brindará una asesoría personalizada sin compromiso. Más de 500 proyectos exitosos nos respaldan.",
      backgroundImage: "/assets/construtop/home/hero-2.webp",
      primaryLabel: "SOLICITAR ASESORÍA GRATUITA",
      primaryHref: "/contacto",
      secondaryLabel: "ESCRIBIR POR WHATSAPP",
      secondaryHref: "https://wa.me/593999307185",
    },
  };

  const sections = [
    { id: "hero", label: "Banner principal", desc: "Textos, botones e imágenes del hero" },
    { id: "stats", label: "KPIs", desc: "Indicadores superiores del Home" },
    { id: "solutions", label: "Soluciones", desc: "Bloque previo a Soluciones" },
    { id: "systems", label: "Sistemas", desc: "Bloque previo a Sistemas" },
    { id: "trust", label: "Confianza", desc: "Bloque de respaldo profesional" },
    { id: "cta", label: "CTA final", desc: "Bloque final de conversión" },
  ];

  const availableIcons = "clock, award, users, shield, check, home, droplets, flower-2, sun, brick-wall, package, settings, wrench, star, target";

  const [form, setForm] = useState(defaultHomeContent);
  const [activeSection, setActiveSection] = useState("hero");
  const [saving, setSaving] = useState(false);

  const deepMerge = (base, override) => {
    if (!override || typeof override !== "object") return base;

    const result = { ...base };

    Object.keys(override).forEach((key) => {
      if (
        override[key] &&
        typeof override[key] === "object" &&
        !Array.isArray(override[key]) &&
        base[key] &&
        typeof base[key] === "object" &&
        !Array.isArray(base[key])
      ) {
        result[key] = deepMerge(base[key], override[key]);
      } else {
        result[key] = override[key];
      }
    });

    return result;
  };

  useEffect(() => {
    getSettings()
      .then((response) => {
        let next = defaultHomeContent;

        try {
          const saved = JSON.parse(response.data?.home_content || "{}");
          next = deepMerge(defaultHomeContent, saved);
        } catch {
          next = defaultHomeContent;
        }

        try {
          const legacyHeroImages = JSON.parse(
            response.data?.home_hero_images || "[]"
          ).filter(Boolean);

          if (legacyHeroImages.length && !response.data?.home_content) {
            next = {
              ...next,
              hero: {
                ...next.hero,
                images: [
                  legacyHeroImages[0] || "",
                  legacyHeroImages[1] || "",
                  legacyHeroImages[2] || "",
                ],
              },
            };
          }
        } catch {}

        setForm(next);
      })
      .catch(() => {
        setForm(defaultHomeContent);
      });
  }, []);

  const updateSectionField = (section, field, value) => {
    setForm((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const updateHeroImage = (index, url) => {
    setForm((prev) => {
      const images = [...(prev.hero.images || [])];
      images[index] = url;

      return {
        ...prev,
        hero: {
          ...prev.hero,
          images,
        },
      };
    });
  };

  const updateStat = (index, field, value) => {
    setForm((prev) => {
      const items = [...(prev.stats.items || [])];

      items[index] = {
        ...items[index],
        [field]: value,
      };

      return {
        ...prev,
        stats: {
          ...prev.stats,
          items,
        },
      };
    });
  };

  const addStat = () => {
    setForm((prev) => ({
      ...prev,
      stats: {
        ...prev.stats,
        items: [
          ...(prev.stats.items || []),
          { icon: "shield", value: "Nuevo", label: "Indicador" },
        ],
      },
    }));
  };

  const removeStat = (index) => {
    setForm((prev) => ({
      ...prev,
      stats: {
        ...prev.stats,
        items: (prev.stats.items || []).filter((_, i) => i !== index),
      },
    }));
  };

  const updateTrustItem = (index, field, value) => {
    setForm((prev) => {
      const items = [...(prev.trust.items || [])];

      items[index] = {
        ...items[index],
        [field]: value,
      };

      return {
        ...prev,
        trust: {
          ...prev.trust,
          items,
        },
      };
    });
  };

  const addTrustItem = () => {
    setForm((prev) => ({
      ...prev,
      trust: {
        ...prev.trust,
        items: [
          ...(prev.trust.items || []),
          {
            icon: "shield",
            title: "Nuevo bloque",
            desc: "Texto editable.",
          },
        ],
      },
    }));
  };

  const removeTrustItem = (index) => {
    setForm((prev) => ({
      ...prev,
      trust: {
        ...prev.trust,
        items: (prev.trust.items || []).filter((_, i) => i !== index),
      },
    }));
  };

  const handleSave = async () => {
    setSaving(true);

    try {
      await updateSetting("home_content", JSON.stringify(form));

      await updateSetting(
        "home_hero_images",
        JSON.stringify((form.hero.images || []).filter(Boolean))
      );

      toast.success("Home actualizado correctamente");
    } catch {
      toast.error("No se pudo guardar el contenido del Home");
    }

    setSaving(false);
  };

  const handleReset = () => {
    const confirmReset = window.confirm(
      "¿Seguro que quieres restaurar el contenido base del Home? Esta acción reemplaza los textos e imágenes configuradas."
    );

    if (!confirmReset) return;

    setForm(defaultHomeContent);
    toast.info("Contenido base restaurado. Guarda para aplicar los cambios.");
  };

  return (
    <div data-testid="home-settings" className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <p className="text-emerald-700 text-xs font-bold uppercase tracking-[0.2em] mb-2">
            Administrador de contenido
          </p>

          <h1 className="font-heading text-3xl text-zinc-900">
            Home administrable
          </h1>

          <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
            Gestiona todos los textos, imágenes, botones, KPIs y bloques visibles
            de la página principal desde una sola interfaz.
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={handleReset}
            className="rounded-none border-zinc-300 text-zinc-700"
          >
            RESTAURAR BASE
          </Button>

          <Button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-none font-bold"
          >
            {saving ? "GUARDANDO..." : "GUARDAR HOME"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[300px_1fr] gap-6">
        <aside className="bg-white border border-zinc-200 p-4 h-fit">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-zinc-400 mb-3">
            Secciones
          </p>

          <div className="space-y-2">
            {sections.map((section) => (
              <button
                key={section.id}
                type="button"
                onClick={() => setActiveSection(section.id)}
                className={`w-full text-left border px-4 py-3 transition-all ${
                  activeSection === section.id
                    ? "border-emerald-700 bg-emerald-50"
                    : "border-zinc-200 hover:border-emerald-300 hover:bg-zinc-50"
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div
                      className={`font-bold text-sm ${
                        activeSection === section.id
                          ? "text-emerald-800"
                          : "text-zinc-800"
                      }`}
                    >
                      {section.label}
                    </div>

                    <div className="text-xs text-zinc-400 mt-0.5">
                      {section.desc}
                    </div>
                  </div>

                  <ChevronRight
                    size={16}
                    className={
                      activeSection === section.id
                        ? "text-emerald-700"
                        : "text-zinc-300"
                    }
                  />
                </div>
              </button>
            ))}
          </div>

          <div className="mt-5 p-3 bg-zinc-50 border border-zinc-100">
            <p className="text-xs text-zinc-500 leading-relaxed">
              Iconos disponibles: {availableIcons}.
            </p>
          </div>
        </aside>

        <section className="bg-white border border-zinc-200 p-5 lg:p-7">
          {activeSection === "hero" && (
            <div className="space-y-6">
              <div>
                <h2 className="font-heading text-2xl text-zinc-900">
                  Banner principal
                </h2>
                <p className="text-sm text-zinc-500 mt-1">
                  Controla el mensaje inicial, botones e imágenes de fondo del
                  slideshow.
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-zinc-700">Texto superior</Label>
                  <Input
                    value={form.hero.eyebrow}
                    onChange={(e) =>
                      updateSectionField("hero", "eyebrow", e.target.value)
                    }
                    className="rounded-none mt-1"
                  />
                </div>

                <div>
                  <Label className="text-sm text-zinc-700">
                    Texto destacado en verde
                  </Label>
                  <Input
                    value={form.hero.highlight}
                    onChange={(e) =>
                      updateSectionField("hero", "highlight", e.target.value)
                    }
                    className="rounded-none mt-1"
                  />
                </div>
              </div>

              <div>
                <Label className="text-sm text-zinc-700">Título principal</Label>
                <Input
                  value={form.hero.title}
                  onChange={(e) =>
                    updateSectionField("hero", "title", e.target.value)
                  }
                  className="rounded-none mt-1"
                />
              </div>

              <div>
                <Label className="text-sm text-zinc-700">Subtítulo</Label>
                <Textarea
                  value={form.hero.subtitle}
                  onChange={(e) =>
                    updateSectionField("hero", "subtitle", e.target.value)
                  }
                  className="rounded-none mt-1"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-zinc-700">
                    Botón principal
                  </Label>
                  <Input
                    value={form.hero.primaryLabel}
                    onChange={(e) =>
                      updateSectionField("hero", "primaryLabel", e.target.value)
                    }
                    className="rounded-none mt-1 mb-2"
                    placeholder="Texto del botón"
                  />
                  <Input
                    value={form.hero.primaryHref}
                    onChange={(e) =>
                      updateSectionField("hero", "primaryHref", e.target.value)
                    }
                    className="rounded-none"
                    placeholder="/soluciones"
                  />
                </div>

                <div>
                  <Label className="text-sm text-zinc-700">
                    Botón secundario
                  </Label>
                  <Input
                    value={form.hero.secondaryLabel}
                    onChange={(e) =>
                      updateSectionField("hero", "secondaryLabel", e.target.value)
                    }
                    className="rounded-none mt-1 mb-2"
                    placeholder="Texto del botón"
                  />
                  <Input
                    value={form.hero.secondaryHref}
                    onChange={(e) =>
                      updateSectionField("hero", "secondaryHref", e.target.value)
                    }
                    className="rounded-none"
                    placeholder="/contacto"
                  />
                </div>
              </div>

              <div className="border-t border-zinc-200 pt-5">
                <Label className="text-sm text-zinc-900 font-bold">
                  Imágenes del hero
                </Label>

                <p className="text-xs text-zinc-400 mt-1 mb-4">
                  Puedes usar hasta 3 imágenes. Si una queda vacía, el sistema la
                  ignora.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[0, 1, 2].map((index) => (
                    <MediaUpload
                      key={index}
                      label={`Imagen Hero ${index + 1}`}
                      value={form.hero.images?.[index] || ""}
                      onChange={(url) => updateHeroImage(index, url)}
                      accept="image/*"
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeSection === "stats" && (
            <div className="space-y-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="font-heading text-2xl text-zinc-900">KPIs</h2>
                  <p className="text-sm text-zinc-500 mt-1">
                    Edita los indicadores que aparecen debajo del banner.
                  </p>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  onClick={addStat}
                  className="rounded-none"
                >
                  <Plus size={14} className="mr-2" />
                  AGREGAR KPI
                </Button>
              </div>

              <div className="space-y-4">
                {(form.stats.items || []).map((item, index) => (
                  <div
                    key={index}
                    className="border border-zinc-200 p-4 bg-zinc-50"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-xs font-bold uppercase tracking-wider text-zinc-500">
                        KPI {index + 1}
                      </p>

                      <button
                        type="button"
                        onClick={() => removeStat(index)}
                        className="text-zinc-400 hover:text-red-500"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs text-zinc-500">Icono</Label>
                        <Input
                          value={item.icon}
                          onChange={(e) =>
                            updateStat(index, "icon", e.target.value)
                          }
                          className="rounded-none mt-1"
                        />
                      </div>

                      <div>
                        <Label className="text-xs text-zinc-500">
                          Texto grande
                        </Label>
                        <Textarea
                          value={item.value}
                          onChange={(e) =>
                            updateStat(index, "value", e.target.value)
                          }
                          className="rounded-none mt-1"
                          rows={2}
                        />
                      </div>

                      <div>
                        <Label className="text-xs text-zinc-500">
                          Texto inferior
                        </Label>
                        <Textarea
                          value={item.label}
                          onChange={(e) =>
                            updateStat(index, "label", e.target.value)
                          }
                          className="rounded-none mt-1"
                          rows={2}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeSection === "solutions" && (
            <div className="space-y-5">
              <div>
                <h2 className="font-heading text-2xl text-zinc-900">
                  Bloque de soluciones
                </h2>
                <p className="text-sm text-zinc-500 mt-1">
                  Controla los textos de introducción y el botón hacia la página
                  de soluciones.
                </p>
              </div>

              <div>
                <Label className="text-sm text-zinc-700">Texto superior</Label>
                <Input
                  value={form.solutionsPreview.eyebrow}
                  onChange={(e) =>
                    updateSectionField("solutionsPreview", "eyebrow", e.target.value)
                  }
                  className="rounded-none mt-1"
                />
              </div>

              <div>
                <Label className="text-sm text-zinc-700">Título</Label>
                <Input
                  value={form.solutionsPreview.title}
                  onChange={(e) =>
                    updateSectionField("solutionsPreview", "title", e.target.value)
                  }
                  className="rounded-none mt-1"
                />
              </div>

              <div>
                <Label className="text-sm text-zinc-700">Descripción</Label>
                <Textarea
                  value={form.solutionsPreview.description}
                  onChange={(e) =>
                    updateSectionField(
                      "solutionsPreview",
                      "description",
                      e.target.value
                    )
                  }
                  className="rounded-none mt-1"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label className="text-sm text-zinc-700">
                    Cantidad visible
                  </Label>
                  <Input
                    type="number"
                    value={form.solutionsPreview.visibleItems}
                    onChange={(e) =>
                      updateSectionField(
                        "solutionsPreview",
                        "visibleItems",
                        Number(e.target.value) || 1
                      )
                    }
                    className="rounded-none mt-1"
                  />
                </div>

                <div>
                  <Label className="text-sm text-zinc-700">Texto botón</Label>
                  <Input
                    value={form.solutionsPreview.ctaLabel}
                    onChange={(e) =>
                      updateSectionField(
                        "solutionsPreview",
                        "ctaLabel",
                        e.target.value
                      )
                    }
                    className="rounded-none mt-1"
                  />
                </div>

                <div>
                  <Label className="text-sm text-zinc-700">Ruta botón</Label>
                  <Input
                    value={form.solutionsPreview.ctaHref}
                    onChange={(e) =>
                      updateSectionField(
                        "solutionsPreview",
                        "ctaHref",
                        e.target.value
                      )
                    }
                    className="rounded-none mt-1"
                  />
                </div>
              </div>
            </div>
          )}

          {activeSection === "systems" && (
            <div className="space-y-5">
              <div>
                <h2 className="font-heading text-2xl text-zinc-900">
                  Bloque de sistemas
                </h2>
                <p className="text-sm text-zinc-500 mt-1">
                  Controla los textos de introducción y el botón hacia sistemas.
                </p>
              </div>

              <div>
                <Label className="text-sm text-zinc-700">Texto superior</Label>
                <Input
                  value={form.systemsPreview.eyebrow}
                  onChange={(e) =>
                    updateSectionField("systemsPreview", "eyebrow", e.target.value)
                  }
                  className="rounded-none mt-1"
                />
              </div>

              <div>
                <Label className="text-sm text-zinc-700">Título</Label>
                <Input
                  value={form.systemsPreview.title}
                  onChange={(e) =>
                    updateSectionField("systemsPreview", "title", e.target.value)
                  }
                  className="rounded-none mt-1"
                />
              </div>

              <div>
                <Label className="text-sm text-zinc-700">Descripción</Label>
                <Textarea
                  value={form.systemsPreview.description}
                  onChange={(e) =>
                    updateSectionField(
                      "systemsPreview",
                      "description",
                      e.target.value
                    )
                  }
                  className="rounded-none mt-1"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm text-zinc-700">Texto botón</Label>
                  <Input
                    value={form.systemsPreview.ctaLabel}
                    onChange={(e) =>
                      updateSectionField("systemsPreview", "ctaLabel", e.target.value)
                    }
                    className="rounded-none mt-1"
                  />
                </div>

                <div>
                  <Label className="text-sm text-zinc-700">Ruta botón</Label>
                  <Input
                    value={form.systemsPreview.ctaHref}
                    onChange={(e) =>
                      updateSectionField("systemsPreview", "ctaHref", e.target.value)
                    }
                    className="rounded-none mt-1"
                  />
                </div>
              </div>
            </div>
          )}

          {activeSection === "trust" && (
            <div className="space-y-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="font-heading text-2xl text-zinc-900">
                    Confianza técnica
                  </h2>
                  <p className="text-sm text-zinc-500 mt-1">
                    Edita el título de la sección y cada tarjeta de respaldo.
                  </p>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  onClick={addTrustItem}
                  className="rounded-none"
                >
                  <Plus size={14} className="mr-2" />
                  AGREGAR TARJETA
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm text-zinc-700">Texto superior</Label>
                  <Input
                    value={form.trust.eyebrow}
                    onChange={(e) =>
                      updateSectionField("trust", "eyebrow", e.target.value)
                    }
                    className="rounded-none mt-1"
                  />
                </div>

                <div>
                  <Label className="text-sm text-zinc-700">Título sección</Label>
                  <Input
                    value={form.trust.title}
                    onChange={(e) =>
                      updateSectionField("trust", "title", e.target.value)
                    }
                    className="rounded-none mt-1"
                  />
                </div>
              </div>

              <div className="space-y-4">
                {(form.trust.items || []).map((item, index) => (
                  <div
                    key={index}
                    className="border border-zinc-200 p-4 bg-zinc-50"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-xs font-bold uppercase tracking-wider text-zinc-500">
                        Tarjeta {index + 1}
                      </p>

                      <button
                        type="button"
                        onClick={() => removeTrustItem(index)}
                        className="text-zinc-400 hover:text-red-500"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-zinc-500">Icono</Label>
                        <Input
                          value={item.icon}
                          onChange={(e) =>
                            updateTrustItem(index, "icon", e.target.value)
                          }
                          className="rounded-none mt-1"
                        />
                      </div>

                      <div>
                        <Label className="text-xs text-zinc-500">Título</Label>
                        <Input
                          value={item.title}
                          onChange={(e) =>
                            updateTrustItem(index, "title", e.target.value)
                          }
                          className="rounded-none mt-1"
                        />
                      </div>
                    </div>

                    <div className="mt-3">
                      <Label className="text-xs text-zinc-500">
                        Descripción
                      </Label>
                      <Textarea
                        value={item.desc}
                        onChange={(e) =>
                          updateTrustItem(index, "desc", e.target.value)
                        }
                        className="rounded-none mt-1"
                        rows={3}
                      />
                      <p className="text-xs text-zinc-400 mt-1">
                        Puedes usar saltos de línea para partir el texto.
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeSection === "cta" && (
            <div className="space-y-5">
              <div>
                <h2 className="font-heading text-2xl text-zinc-900">
                  CTA final
                </h2>
                <p className="text-sm text-zinc-500 mt-1">
                  Controla el bloque final de conversión del Home.
                </p>
              </div>

              <div>
                <Label className="text-sm text-zinc-700">Título</Label>
                <Input
                  value={form.cta.title}
                  onChange={(e) =>
                    updateSectionField("cta", "title", e.target.value)
                  }
                  className="rounded-none mt-1"
                />
              </div>

              <div>
                <Label className="text-sm text-zinc-700">Descripción</Label>
                <Textarea
                  value={form.cta.description}
                  onChange={(e) =>
                    updateSectionField("cta", "description", e.target.value)
                  }
                  className="rounded-none mt-1"
                  rows={3}
                />
              </div>

              <MediaUpload
                label="Imagen de fondo"
                value={form.cta.backgroundImage}
                onChange={(url) =>
                  updateSectionField("cta", "backgroundImage", url)
                }
                accept="image/*"
              />

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-zinc-700">
                    Botón principal
                  </Label>
                  <Input
                    value={form.cta.primaryLabel}
                    onChange={(e) =>
                      updateSectionField("cta", "primaryLabel", e.target.value)
                    }
                    className="rounded-none mt-1 mb-2"
                    placeholder="Texto del botón"
                  />
                  <Input
                    value={form.cta.primaryHref}
                    onChange={(e) =>
                      updateSectionField("cta", "primaryHref", e.target.value)
                    }
                    className="rounded-none"
                    placeholder="/contacto"
                  />
                </div>

                <div>
                  <Label className="text-sm text-zinc-700">
                    Botón secundario
                  </Label>
                  <Input
                    value={form.cta.secondaryLabel}
                    onChange={(e) =>
                      updateSectionField("cta", "secondaryLabel", e.target.value)
                    }
                    className="rounded-none mt-1 mb-2"
                    placeholder="Texto del botón"
                  />
                  <Input
                    value={form.cta.secondaryHref}
                    onChange={(e) =>
                      updateSectionField("cta", "secondaryHref", e.target.value)
                    }
                    className="rounded-none"
                    placeholder="https://wa.me/593999307185"
                  />
                </div>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function GenericForm({ fields, item, onSubmit, onCancel }) {
  const [form, setForm] = useState(() => {
    const init = {};
    fields.forEach(f => { init[f.key] = item ? (item[f.key] ?? "") : (f.default ?? ""); });
    return init;
  });

  const handleChange = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(form); }} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
      {fields.map(f => (
        <div key={f.key}>
          {f.type === "media" ? (
            <MediaUpload
              label={f.label}
              value={form[f.key]}
              onChange={(url) => handleChange(f.key, url)}
              accept={f.accept || "image/*,application/pdf"}
            />
          ) : f.type === "textarea" ? (
            <>
              <Label className="text-zinc-700 text-sm">{f.label}{f.required && " *"}</Label>
              <Textarea value={form[f.key]} onChange={e => handleChange(f.key, e.target.value)} className="rounded-none mt-1 text-sm" rows={3} required={f.required} />
            </>
          ) : f.type === "select" ? (
            <>
              <Label className="text-zinc-700 text-sm">{f.label}{f.required && " *"}</Label>
              <select
                value={form[f.key]}
                onChange={e => handleChange(f.key, e.target.value)}
                className="w-full rounded-none mt-1 text-sm border border-zinc-300 bg-white h-9 px-3"
                required={f.required}
              >
                <option value="">Seleccione...</option>
                {(f.options || []).map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </>
          ) : f.type === "url_or_upload" ? (
            <div>
              <Label className="text-zinc-700 text-sm">{f.label}</Label>
              <Input
                value={form[f.key]}
                onChange={e => handleChange(f.key, e.target.value)}
                className="rounded-none mt-1 text-sm mb-1"
                placeholder="URL de imagen o use el boton para subir"
              />
              <MediaUpload
                label=""
                value=""
                onChange={(url) => handleChange(f.key, url)}
                accept="image/*"
              />
            </div>
          ) : (
            <>
              <Label className="text-zinc-700 text-sm">{f.label}{f.required && " *"}</Label>
              <Input value={form[f.key]} onChange={e => handleChange(f.key, e.target.value)} className="rounded-none mt-1 text-sm" type={f.type || "text"} required={f.required} />
            </>
          )}
        </div>
      ))}
      <div className="flex gap-3 pt-2">
        <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-none text-sm font-bold flex-1">
          {item ? "ACTUALIZAR" : "CREAR"}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} className="rounded-none text-sm flex-1">CANCELAR</Button>
      </div>
    </form>
  );
}

/* ── List inputs that preserve raw text while editing ──
   Storing only the parsed array would strip trailing commas/spaces/newlines
   on every keystroke (e.g. "iso 9001, " would become "iso 9001" and the
   user can't type the next chip). These wrappers keep the raw text local
   and only push the parsed array up to the parent form. */
function CommaListInput({ value, onChange, ...props }) {
  const [text, setText] = useState(() => (value || []).join(", "));
  return (
    <Input
      {...props}
      value={text}
      onChange={e => {
        setText(e.target.value);
        onChange(e.target.value.split(",").map(s => s.trim()).filter(Boolean));
      }}
    />
  );
}

function LinesTextarea({ value, onChange, map, format, ...props }) {
  const toText = format || (arr => (arr || []).join("\n"));
  const fromLine = map || (line => line);
  const [text, setText] = useState(() => toText(value));
  return (
    <Textarea
      {...props}
      value={text}
      onChange={e => {
        setText(e.target.value);
        const arr = e.target.value
          .split("\n")
          .map(s => s.trim())
          .filter(Boolean)
          .map(fromLine);
        onChange(arr);
      }}
    />
  );
}

/* ── Solution Form (nested subcategories/systems + application steps) ── */
function SolutionForm({ item, onSubmit, onCancel }) {
  const [form, setForm] = useState(() => ({
    title: item?.title || "",
    slug: item?.slug || "",
    description: item?.description || "",
    image_url: item?.image_url || "",
    icon: item?.icon || "hard-hat",
    order: item?.order || 0,
    subcategories: item?.subcategories || [],
    application_steps: item?.application_steps || [],
  }));

  const handleFieldChange = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const addSubcategory = () => {
    setForm(prev => ({
      ...prev,
      subcategories: [...prev.subcategories, { name: "", description: "", systems: [] }]
    }));
  };

  const updateSubcategory = (idx, key, value) => {
    setForm(prev => {
      const subs = [...prev.subcategories];
      subs[idx] = { ...subs[idx], [key]: value };
      return { ...prev, subcategories: subs };
    });
  };

  const removeSubcategory = (idx) => {
    setForm(prev => ({
      ...prev,
      subcategories: prev.subcategories.filter((_, i) => i !== idx)
    }));
  };

  const addSystem = (subIdx) => {
    setForm(prev => {
      const subs = [...prev.subcategories];
      subs[subIdx] = {
        ...subs[subIdx],
        systems: [...(subs[subIdx].systems || []), { name: "", image_url: "", layers: [], materials: [] }]
      };
      return { ...prev, subcategories: subs };
    });
  };

  const updateSystem = (subIdx, sysIdx, key, value) => {
    setForm(prev => {
      const subs = [...prev.subcategories];
      const systems = [...(subs[subIdx].systems || [])];
      systems[sysIdx] = { ...systems[sysIdx], [key]: value };
      subs[subIdx] = { ...subs[subIdx], systems };
      return { ...prev, subcategories: subs };
    });
  };

  const removeSystem = (subIdx, sysIdx) => {
    setForm(prev => {
      const subs = [...prev.subcategories];
      subs[subIdx] = {
        ...subs[subIdx],
        systems: subs[subIdx].systems.filter((_, i) => i !== sysIdx)
      };
      return { ...prev, subcategories: subs };
    });
  };

  const addApplicationStep = () => {
    setForm(prev => ({
      ...prev,
      application_steps: [
        ...(prev.application_steps || []),
        { number: String((prev.application_steps || []).length + 1), image_url: "", title: "", description: "" }
      ]
    }));
  };

  const updateApplicationStep = (idx, key, value) => {
    setForm(prev => {
      const steps = [...(prev.application_steps || [])];
      steps[idx] = { ...steps[idx], [key]: value };
      return { ...prev, application_steps: steps };
    });
  };

  const removeApplicationStep = (idx) => {
    setForm(prev => ({
      ...prev,
      application_steps: (prev.application_steps || []).filter((_, i) => i !== idx)
    }));
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(form); }} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-zinc-700 text-sm">Titulo *</Label>
          <Input value={form.title} onChange={e => handleFieldChange("title", e.target.value)} className="rounded-none mt-1 text-sm" required />
        </div>
        <div>
          <Label className="text-zinc-700 text-sm">Slug *</Label>
          <Input value={form.slug} onChange={e => handleFieldChange("slug", e.target.value)} className="rounded-none mt-1 text-sm" required />
        </div>
      </div>
      <div>
        <Label className="text-zinc-700 text-sm">Descripcion *</Label>
        <Textarea value={form.description} onChange={e => handleFieldChange("description", e.target.value)} className="rounded-none mt-1 text-sm" rows={2} required />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-zinc-700 text-sm">Icono</Label>
          <Input value={form.icon} onChange={e => handleFieldChange("icon", e.target.value)} className="rounded-none mt-1 text-sm" />
        </div>
        <div>
          <Label className="text-zinc-700 text-sm">Orden</Label>
          <Input type="number" value={form.order} onChange={e => handleFieldChange("order", parseInt(e.target.value) || 0)} className="rounded-none mt-1 text-sm" />
        </div>
      </div>
      <MediaUpload label="Imagen principal de la solucion" value={form.image_url} onChange={(url) => handleFieldChange("image_url", url)} accept="image/*" />

      {/* Subcategories */}
      <div className="border-t border-zinc-200 pt-4">
        <div className="flex items-center justify-between mb-3">
          <Label className="text-zinc-900 font-bold text-sm">Subcategorias ({form.subcategories.length})</Label>
          <Button type="button" size="sm" variant="outline" onClick={addSubcategory} className="rounded-none text-xs h-7">
            <Plus size={12} className="mr-1" /> Agregar
          </Button>
        </div>
        {form.subcategories.map((sub, si) => (
          <div key={si} className="border border-zinc-200 p-3 mb-3 bg-zinc-50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold text-zinc-500">Subcategoria {si + 1}</span>
              <button type="button" onClick={() => removeSubcategory(si)} className="text-red-400 hover:text-red-600"><Trash2 size={12} /></button>
            </div>
            <Input value={sub.name} onChange={e => updateSubcategory(si, "name", e.target.value)} placeholder="Nombre de subcategoria" className="rounded-none text-sm mb-2" />
            <Input value={sub.description || ""} onChange={e => updateSubcategory(si, "description", e.target.value)} placeholder="Descripcion (opcional)" className="rounded-none text-sm mb-2" />
            <div className="ml-3 border-l-2 border-emerald-200 pl-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-zinc-500">Sistemas ({(sub.systems || []).length})</span>
                <button type="button" onClick={() => addSystem(si)} className="text-xs text-emerald-600 hover:underline">+ Sistema</button>
              </div>
              {(sub.systems || []).map((sys, syi) => (
                <div key={syi} className="bg-white border border-zinc-200 p-2 mb-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-zinc-400">Sistema {syi + 1}</span>
                    <button type="button" onClick={() => removeSystem(si, syi)} className="text-red-400 hover:text-red-600"><X size={10} /></button>
                  </div>
                  <Input value={sys.name} onChange={e => updateSystem(si, syi, "name", e.target.value)} placeholder="Nombre del sistema" className="rounded-none text-xs mb-1 h-7" />
                  <MediaUpload label="Imagen del sistema" value={sys.image_url || ""} onChange={(url) => updateSystem(si, syi, "image_url", url)} accept="image/*" />
                  <LinesTextarea value={sys.layers || []} onChange={(arr) => updateSystem(si, syi, "layers", arr)} format={(arr) => (arr || []).map(l => l.name || l).join("\n")} map={(line) => ({ name: line })} placeholder="Capas (una por linea)" className="rounded-none text-xs mb-1" rows={2} />
                  <CommaListInput value={sys.materials || []} onChange={(arr) => updateSystem(si, syi, "materials", arr)} placeholder="Materiales separados por coma" className="rounded-none text-xs h-7" />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Application steps */}
      <div className="border-t border-zinc-200 pt-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <Label className="text-zinc-900 font-bold text-sm">Aplicacion del sistema ({(form.application_steps || []).length})</Label>
            <p className="text-xs text-zinc-400 mt-1">Agrega una secuencia tipo Foto + numero + descripcion para esta solucion.</p>
          </div>
          <Button type="button" size="sm" variant="outline" onClick={addApplicationStep} className="rounded-none text-xs h-7">
            <Plus size={12} className="mr-1" /> Paso
          </Button>
        </div>

        {(form.application_steps || []).map((step, idx) => (
          <div key={idx} className="border border-zinc-200 p-3 mb-3 bg-zinc-50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold text-zinc-500">Paso {idx + 1}</span>
              <button type="button" onClick={() => removeApplicationStep(idx)} className="text-red-400 hover:text-red-600"><Trash2 size={12} /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[90px_1fr] gap-2 mb-2">
              <Input value={step.number || ""} onChange={e => updateApplicationStep(idx, "number", e.target.value)} placeholder="Nro." className="rounded-none text-sm" />
              <Input value={step.title || ""} onChange={e => updateApplicationStep(idx, "title", e.target.value)} placeholder="Titulo corto opcional" className="rounded-none text-sm" />
            </div>
            <MediaUpload label="Foto del paso" value={step.image_url || ""} onChange={(url) => updateApplicationStep(idx, "image_url", url)} accept="image/*" />
            <Textarea value={step.description || ""} onChange={e => updateApplicationStep(idx, "description", e.target.value)} placeholder="Descripcion del paso" className="rounded-none text-sm mt-2" rows={2} />
          </div>
        ))}
      </div>

      <div className="flex gap-3 pt-2">
        <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-none text-sm font-bold flex-1">
          {item ? "ACTUALIZAR" : "CREAR"}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} className="rounded-none text-sm flex-1">CANCELAR</Button>
      </div>
    </form>
  );
}

/* ── System Form (products) ── */
function SystemForm({ item, onSubmit, onCancel }) {
  const [form, setForm] = useState(() => ({
    title: item?.title || "",
    slug: item?.slug || "",
    description: item?.description || "",
    technical_description: item?.technical_description || "",
    image_url: item?.image_url || "",
    technical_images: item?.technical_images || [],
    before_image: item?.before_image || "",
    after_image: item?.after_image || "",
    system_pdf_url: item?.system_pdf_url || "",
    order: item?.order || 0,
    advantages: item?.advantages || [],
    products: item?.products || [],
    certifications: item?.certifications || [],
  }));

  const handleFieldChange = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const addTechnicalImage = (url) => {
    if (!url) return;
    setForm(prev => ({ ...prev, technical_images: [...(prev.technical_images || []), url] }));
  };

  const removeTechnicalImage = (idx) => {
    setForm(prev => ({ ...prev, technical_images: (prev.technical_images || []).filter((_, i) => i !== idx) }));
  };

  const addProduct = () => {
    setForm(prev => ({
      ...prev,
      products: [...prev.products, { name: "", image_url: "", description: "", advantages: [], presentation: "", yield_info: "" }]
    }));
  };

  const updateProduct = (idx, key, value) => {
    setForm(prev => {
      const prods = [...prev.products];
      prods[idx] = { ...prods[idx], [key]: value };
      return { ...prev, products: prods };
    });
  };

  const removeProduct = (idx) => {
    setForm(prev => ({ ...prev, products: prev.products.filter((_, i) => i !== idx) }));
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(form); }} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-zinc-700 text-sm">Titulo *</Label>
          <Input value={form.title} onChange={e => handleFieldChange("title", e.target.value)} className="rounded-none mt-1 text-sm" required />
        </div>
        <div>
          <Label className="text-zinc-700 text-sm">Slug *</Label>
          <Input value={form.slug} onChange={e => handleFieldChange("slug", e.target.value)} className="rounded-none mt-1 text-sm" required />
        </div>
      </div>
      <div>
        <Label className="text-zinc-700 text-sm">Descripcion corta *</Label>
        <Textarea value={form.description} onChange={e => handleFieldChange("description", e.target.value)} className="rounded-none mt-1 text-sm" rows={2} required />
      </div>
      <div>
        <Label className="text-zinc-700 text-sm">Parrafo tecnico junto a la imagen de ficha</Label>
        <Textarea
          value={form.technical_description || ""}
          onChange={e => handleFieldChange("technical_description", e.target.value)}
          className="rounded-none mt-1 text-sm"
          rows={4}
          placeholder="Texto editable que aparecera a la derecha de la imagen de ficha tecnica del sistema."
        />
      </div>
      <div>
        <Label className="text-zinc-700 text-sm">Orden</Label>
        <Input type="number" value={form.order} onChange={e => handleFieldChange("order", parseInt(e.target.value) || 0)} className="rounded-none mt-1 text-sm" />
      </div>
      <MediaUpload label="Imagen de ficha tecnica del sistema (respaldo si no hay galeria)" value={form.image_url} onChange={(url) => handleFieldChange("image_url", url)} accept="image/*" />

      <div className="border border-zinc-200 p-3 bg-zinc-50">
        <Label className="text-zinc-900 font-bold text-sm mb-2 block">
          Galeria de imagenes de ficha tecnica ({(form.technical_images || []).length})
        </Label>
        {(form.technical_images || []).length > 0 && (
          <div className="grid grid-cols-3 gap-2 mb-3">
            {(form.technical_images || []).map((img, ti) => (
              <div key={ti} className="relative border border-zinc-200 bg-white h-20 overflow-hidden">
                <img src={resolveMedia(img)} alt={`Ficha ${ti + 1}`} className="w-full h-full object-contain" />
                <button
                  type="button"
                  onClick={() => removeTechnicalImage(ti)}
                  className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white p-1 transition-colors"
                  aria-label={`Eliminar imagen ${ti + 1}`}
                >
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
        <MediaUpload label="Agregar imagen a la galeria" value="" onChange={addTechnicalImage} accept="image/*" />
        <p className="text-xs text-zinc-400 mt-2">Si agrega imagenes aqui, la pagina publica mostrara una galeria con flechas de navegacion en lugar de la imagen unica.</p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <MediaUpload label="Imagen ANTES" value={form.before_image} onChange={(url) => handleFieldChange("before_image", url)} accept="image/*" />
        <MediaUpload label="Imagen DESPUES" value={form.after_image} onChange={(url) => handleFieldChange("after_image", url)} accept="image/*" />
      </div>
      <p className="text-xs text-zinc-400 -mt-2">Si sube ambas imagenes (antes/despues), se mostrara el comparador a la derecha de la ficha tecnica del sistema.</p>
      <MediaUpload label="PDF de ficha del sistema" value={form.system_pdf_url || ""} onChange={(url) => handleFieldChange("system_pdf_url", url)} accept="application/pdf" />
      <div>
        <Label className="text-zinc-700 text-sm">Ventajas del sistema (una por linea)</Label>
        <LinesTextarea
          value={form.advantages}
          onChange={(arr) => handleFieldChange("advantages", arr)}
          className="rounded-none mt-1 text-sm"
          rows={3}
        />
      </div>
      <div>
        <Label className="text-zinc-700 text-sm">Certificaciones (separadas por coma)</Label>
        <CommaListInput
          value={form.certifications}
          onChange={(arr) => handleFieldChange("certifications", arr)}
          className="rounded-none mt-1 text-sm"
        />
      </div>

      {/* Products */}
      <div className="border-t border-zinc-200 pt-4">
        <div className="flex items-center justify-between mb-3">
          <Label className="text-zinc-900 font-bold text-sm">Productos ({form.products.length})</Label>
          <Button type="button" size="sm" variant="outline" onClick={addProduct} className="rounded-none text-xs h-7">
            <Plus size={12} className="mr-1" /> Agregar
          </Button>
        </div>
        {form.products.map((prod, pi) => (
          <div key={pi} className="border border-zinc-200 p-3 mb-3 bg-zinc-50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold text-zinc-500">Producto {pi + 1}</span>
              <button type="button" onClick={() => removeProduct(pi)} className="text-red-400 hover:text-red-600"><Trash2 size={12} /></button>
            </div>
            <Input value={prod.name} onChange={e => updateProduct(pi, "name", e.target.value)} placeholder="Nombre del producto" className="rounded-none text-sm mb-2" />
            <MediaUpload
              label="Imagen del producto"
              value={prod.image_url || ""}
              onChange={(url) => updateProduct(pi, "image_url", url)}
              accept="image/*"
            />
            <Textarea value={prod.description || ""} onChange={e => updateProduct(pi, "description", e.target.value)} placeholder="Descripcion" className="rounded-none text-sm mb-2" rows={2} />
            <LinesTextarea
              value={prod.advantages || []}
              onChange={(arr) => updateProduct(pi, "advantages", arr)}
              placeholder="Ventajas (una por linea)"
              className="rounded-none text-sm mb-2"
              rows={2}
            />
            <div className="grid grid-cols-2 gap-2">
              <Input value={prod.presentation || ""} onChange={e => updateProduct(pi, "presentation", e.target.value)} placeholder="Presentacion" className="rounded-none text-xs h-7" />
              <Input value={prod.yield_info || ""} onChange={e => updateProduct(pi, "yield_info", e.target.value)} placeholder="Rendimiento" className="rounded-none text-xs h-7" />
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-3 pt-2">
        <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-none text-sm font-bold flex-1">
          {item ? "ACTUALIZAR" : "CREAR"}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} className="rounded-none text-sm flex-1">CANCELAR</Button>
      </div>
    </form>
  );
}


/* ── Project Form (gallery based) ── */
function ProjectForm({ item, onSubmit, onCancel }) {
  const [form, setForm] = useState(() => ({
    title: item?.title || "",
    description: item?.description || "",
    location: item?.location || "",
    category: item?.category || "",
    year: item?.year || "",
    images: item?.images || [],
    before_image: item?.before_image || "",
    after_image: item?.after_image || "",
  }));

  const update = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const addImage = (url) => {
    if (!url) return;
    setForm((prev) => ({
      ...prev,
      images: [...(prev.images || []), url],
    }));
  };

  const removeImage = (idx) => {
    setForm((prev) => ({
      ...prev,
      images: (prev.images || []).filter((_, i) => i !== idx),
    }));
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(form);
      }}
      className="space-y-4 max-h-[70vh] overflow-y-auto pr-2"
    >
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-zinc-700 text-sm">Título *</Label>
          <Input
            value={form.title}
            onChange={(e) => update("title", e.target.value)}
            className="rounded-none mt-1 text-sm"
            required
          />
        </div>

        <div>
          <Label className="text-zinc-700 text-sm">Categoría</Label>
          <Input
            value={form.category}
            onChange={(e) => update("category", e.target.value)}
            className="rounded-none mt-1 text-sm"
          />
        </div>
      </div>

      <div>
        <Label className="text-zinc-700 text-sm">
          Descripción / especificación editable *
        </Label>
        <Textarea
          value={form.description}
          onChange={(e) => update("description", e.target.value)}
          className="rounded-none mt-1 text-sm"
          rows={3}
          required
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-zinc-700 text-sm">Ubicación</Label>
          <Input
            value={form.location}
            onChange={(e) => update("location", e.target.value)}
            className="rounded-none mt-1 text-sm"
          />
        </div>

        <div>
          <Label className="text-zinc-700 text-sm">Año</Label>
          <Input
            value={form.year}
            onChange={(e) => update("year", e.target.value)}
            className="rounded-none mt-1 text-sm"
          />
        </div>
      </div>

      <div className="border-t border-zinc-200 pt-4">
        <Label className="text-zinc-900 font-bold text-sm">
          Galería de imágenes ({(form.images || []).length})
        </Label>

        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
          {(form.images || []).map((img, idx) => (
            <div
              key={idx}
              className="relative border border-zinc-200 bg-zinc-50 h-28 overflow-hidden"
            >
              <img
                src={resolveMedia(img)}
                alt={`Proyecto ${idx + 1}`}
                className="w-full h-full object-cover"
              />

              <button
                type="button"
                onClick={() => removeImage(idx)}
                className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white p-1 transition-colors"
                aria-label={`Eliminar imagen ${idx + 1}`}
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>

        <div className="mt-3">
          <MediaUpload
            label="Agregar imagen"
            value=""
            onChange={addImage}
            accept="image/*"
          />
        </div>
      </div>

      <div className="flex gap-3 pt-2">
        <Button
          type="submit"
          className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-none text-sm font-bold flex-1"
        >
          {item ? "ACTUALIZAR" : "CREAR"}
        </Button>

        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          className="rounded-none text-sm flex-1"
        >
          CANCELAR
        </Button>
      </div>
    </form>
  );
}

/* ── Instagram Settings Panel ── */
function InstagramSettings() {
  const [igUrl, setIgUrl] = useState("");
  const [igEmbed, setIgEmbed] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getSettings().then(r => {
      setIgUrl(r.data?.instagram_url || "");
      setIgEmbed(r.data?.instagram_embed || "");
    }).catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSetting("instagram_url", igUrl);
      await updateSetting("instagram_embed", igEmbed);
      toast.success("Configuracion de Instagram guardada");
    } catch {
      toast.error("Error al guardar");
    }
    setSaving(false);
  };

  return (
    <div data-testid="instagram-settings">
      <h1 className="font-heading text-2xl text-zinc-900 mb-6">Configuracion de Instagram</h1>
      <div className="bg-white border border-zinc-200 p-6 max-w-2xl space-y-5">
        <div>
          <Label className="text-zinc-700 text-sm">URL del perfil de Instagram</Label>
          <Input
            data-testid="ig-url-input"
            value={igUrl}
            onChange={e => setIgUrl(e.target.value)}
            placeholder="https://www.instagram.com/construtop/"
            className="rounded-none mt-1 text-sm"
          />
          <p className="text-xs text-zinc-400 mt-1">Si no hay codigo embed, se mostrara un enlace al perfil.</p>
        </div>
        <div>
          <Label className="text-zinc-700 text-sm">Codigo Embed de Instagram (opcional)</Label>
          <Textarea
            data-testid="ig-embed-input"
            value={igEmbed}
            onChange={e => setIgEmbed(e.target.value)}
            placeholder='<iframe src="..." ...></iframe>'
            className="rounded-none mt-1 text-sm font-mono"
            rows={5}
          />
          <p className="text-xs text-zinc-400 mt-1">Pegue el codigo embed de Instagram aqui. Tiene prioridad sobre la URL.</p>
        </div>
        <Button
          data-testid="ig-save-btn"
          onClick={handleSave}
          disabled={saving}
          className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-none font-bold"
        >
          {saving ? "GUARDANDO..." : "GUARDAR CONFIGURACION"}
        </Button>
      </div>
    </div>
  );
}

export default function AdminDashboardPage() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [stats, setStats] = useState({});
  const [data, setData] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    getMe().then(r => { setUser(r.data); setChecking(false); })
      .catch(() => { setChecking(false); navigate("/admin/login"); });
  }, [navigate]);

  const loadData = useCallback(async () => {
    try {
      const loaders = {
        dashboard: () => getStats().then(r => setStats(r.data)),
        solutions: () => getSolutions().then(r => setData(r.data)),
        systems: () => getSystems().then(r => setData(r.data)),
        sheets: () => getSheets().then(r => setData(r.data)),
        projects: () => getProjects().then(r => setData(r.data)),
        distributors: () => getDistributors().then(r => setData(r.data)),
        contacts: () => getContacts().then(r => setData(r.data)),
        leads: () => getLeads().then(r => setData(r.data)),
      };
      if (loaders[activeTab]) await loaders[activeTab]();
    } catch (err) {
      if (err.response?.status === 401) navigate("/admin/login");
    }
  }, [activeTab, navigate]);

  useEffect(() => { if (user) loadData(); }, [user, loadData]);

  const handleLogout = async () => {
    await apiLogout();
    localStorage.removeItem("admin_token");
    navigate("/admin/login");
  };

  const handleCreate = () => { setEditItem(null); setDialogOpen(true); };
  const handleEdit = (item) => { setEditItem(item); setDialogOpen(true); };
  const handleDelete = async (item) => {
    if (!window.confirm("Eliminar este registro?")) return;
    const deleters = { solutions: deleteSolution, systems: deleteSystem, sheets: deleteSheet, projects: deleteProject, distributors: deleteDistributor };
    try {
      await deleters[activeTab](item.id);
      toast.success("Eliminado");
      loadData();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const handleSubmit = async (formData) => {
    const creators = { solutions: createSolution, systems: createSystem, sheets: createSheet, projects: createProject, distributors: createDistributor };
    const updaters = { solutions: updateSolution, systems: updateSystem, sheets: updateSheet, projects: updateProject, distributors: updateDistributor };
    try {
      if (editItem) await updaters[activeTab](editItem.id, formData);
      else await creators[activeTab](formData);
      toast.success(editItem ? "Actualizado" : "Creado");
      setDialogOpen(false);
      loadData();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const getConfig = () => {
    const configs = {
      solutions: {
        columns: [
          { key: "title", label: "Titulo" },
          { key: "slug", label: "Slug" },
          { key: "subcategories", label: "Subcategorias", render: (v) => (v || []).length },
          { key: "order", label: "Orden" }
        ],
        customForm: true,
      },
      systems: {
        columns: [
          { key: "title", label: "Titulo" },
          { key: "slug", label: "Slug" },
          { key: "products", label: "Productos", render: (v) => (v || []).length },
          { key: "order", label: "Orden" }
        ],
        customForm: true,
      },
      sheets: {
        columns: [{ key: "title", label: "Titulo" }, { key: "category", label: "Categoria" }, { key: "system_type", label: "Sistema" }],
        fields: [
          { key: "title", label: "Titulo", required: true },
          { key: "category", label: "Categoria", required: true, type: "select", options: ["General", "METIC", "MPM"] },
          { key: "system_type", label: "Tipo de sistema" }, { key: "work_type", label: "Tipo de obra" },
          { key: "image_url", label: "Imagen de la ficha", type: "media", accept: "image/*" },
          { key: "file_url", label: "Archivo PDF", type: "url_or_upload" }, { key: "description", label: "Descripcion", type: "textarea" },
        ],
      },
      projects: {
        columns: [{ key: "title", label: "Titulo" }, { key: "location", label: "Ubicacion" }, { key: "category", label: "Categoria" }, { key: "images", label: "Imagenes", render: (v) => (v || []).length }],
        customForm: true,
      },

      distributors: {
        columns: [
          { key: "name", label: "Nombre" },
          { key: "city", label: "Ciudad" },
          { key: "phone", label: "Teléfono" },
          {
            key: "website_url",
            label: "Página",
            render: (value) => value ? "Configurada" : "Sin enlace",
          },
        ],
        fields: [
          {
            key: "name",
            label: "Nombre",
            required: true,
          },
          {
            key: "city",
            label: "Ciudad",
            required: true,
          },
          {
            key: "province",
            label: "Provincia",
          },
          {
            key: "address",
            label: "Dirección",
          },
          {
            key: "phone",
            label: "Teléfono",
          },
          {
            key: "email",
            label: "Email",
          },
          {
            key: "website_url",
            label: "Página web del distribuidor",
          },
          {
            key: "logo_url",
            label: "Logo",
            type: "url_or_upload",
          },
        ],
      },
      contacts: {
        columns: [{ key: "name", label: "Nombre" }, { key: "email", label: "Email" }, { key: "subject", label: "Asunto" }, { key: "created_at", label: "Fecha", render: (v) => v ? new Date(v).toLocaleDateString("es-EC") : "" }],
        fields: [],
      },
      leads: {
        columns: [
          { key: "name", label: "Nombre" },
          { key: "whatsapp", label: "WhatsApp" },
          { key: "source", label: "Fuente" },
          { key: "created_at", label: "Fecha", render: (v) => v ? new Date(v).toLocaleDateString("es-EC") : "" }
        ],
        fields: [],
      },
    };
    return configs[activeTab] || { columns: [], fields: [] };
  };

  if (checking) return <div className="min-h-screen bg-zinc-100 flex items-center justify-center"><div className="text-zinc-500">Cargando...</div></div>;
  if (!user) return null;

  const config = getConfig();
  const canCreate = !["dashboard", "home", "contacts", "leads", "instagram"].includes(activeTab);

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-zinc-100 flex">
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-zinc-900 text-white rounded"
        data-testid="admin-sidebar-toggle"
      >
        {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      <aside className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-zinc-950 border-r border-zinc-800 transform transition-transform lg:translate-x-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="p-6 border-b border-zinc-800">
          <img src={LOGO_URL} alt="Construtop" className="h-8 mb-2" />
          <p className="text-zinc-500 text-xs">Panel de Administracion</p>
        </div>
        <div className="p-3 border-b border-zinc-800">
          <a href="/" data-testid="admin-back-home" className="flex items-center gap-2 px-4 py-2 text-sm text-zinc-400 hover:text-emerald-400 hover:bg-zinc-900 rounded transition-colors">
            <Home size={16} /> Volver al inicio
          </a>
        </div>
        <nav className="p-3 space-y-0.5">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); setData([]); setSidebarOpen(false); }}
              data-testid={`admin-tab-${tab.id}`}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm rounded transition-colors ${
                activeTab === tab.id ? "bg-emerald-600/10 text-emerald-400" : "text-zinc-400 hover:text-white hover:bg-zinc-900"
              }`}
            >
              <tab.icon size={16} />
              {tab.label}
            </button>
          ))}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-zinc-800">
          <div className="text-xs text-zinc-500 mb-3">{user.email}</div>
          <button onClick={handleLogout} data-testid="admin-logout-btn" className="flex items-center gap-2 text-zinc-400 hover:text-red-400 text-sm transition-colors">
            <LogOut size={14} /> Cerrar sesion
          </button>
        </div>
      </aside>

      <main className="flex-1 p-4 lg:p-8 lg:ml-0">
        <div className="max-w-6xl mx-auto">
          {activeTab === "dashboard" ? (
            <div>
              <h1 className="font-heading text-2xl text-zinc-900 mb-8">Dashboard</h1>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
                {[
                  { label: "Soluciones", value: stats.solutions, icon: Layers },
                  { label: "Sistemas", value: stats.systems, icon: FolderOpen },
                  { label: "Fichas Tecnicas", value: stats.technical_sheets, icon: FileText },
                  { label: "Proyectos", value: stats.projects, icon: Building2 },
                  { label: "Distribuidores", value: stats.distributors, icon: MapPin },
                  { label: "Contactos", value: stats.contacts, icon: Mail },
                  { label: "Leads", value: stats.leads, icon: Users },
                ].map((s, i) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 20, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ delay: i * 0.06, type: "spring", stiffness: 200 }} className="bg-white border border-zinc-200 p-5 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-3">
                      <s.icon size={18} className="text-emerald-600" />
                      <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.3 + i * 0.06, type: "spring", stiffness: 300 }} className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5">Activo</motion.span>
                    </div>
                    <AnimatedCounter target={s.value ?? 0} delay={i * 80} />
                    <div className="text-xs text-zinc-500 mt-1">{s.label}</div>
                  </motion.div>
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }} className="bg-white border border-zinc-200 p-6">
                  <div className="flex items-center gap-2 mb-6">
                    <BarChart3 size={18} className="text-emerald-600" />
                    <h3 className="font-heading text-sm text-zinc-900">Distribucion de contenido</h3>
                  </div>
                  <div className="space-y-4">
                    {[
                      { label: "Soluciones", value: stats.solutions ?? 0, max: 10, color: "bg-emerald-500" },
                      { label: "Sistemas", value: stats.systems ?? 0, max: 5, color: "bg-blue-500" },
                      { label: "Fichas Tecnicas", value: stats.technical_sheets ?? 0, max: 20, color: "bg-amber-500" },
                      { label: "Proyectos", value: stats.projects ?? 0, max: 10, color: "bg-violet-500" },
                      { label: "Distribuidores", value: stats.distributors ?? 0, max: 10, color: "bg-cyan-500" },
                    ].map((bar, i) => (
                      <div key={i}>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-zinc-600">{bar.label}</span>
                          <span className="font-bold text-zinc-900">{bar.value}</span>
                        </div>
                        <div className="h-3 bg-zinc-100 overflow-hidden">
                          <motion.div initial={{ width: 0 }} animate={{ width: `${Math.min((bar.value / bar.max) * 100, 100)}%` }} transition={{ delay: 0.5 + i * 0.1, duration: 0.8, ease: "easeOut" }} className={`h-full ${bar.color}`} />
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>

                <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 }} className="bg-white border border-zinc-200 p-6">
                  <div className="flex items-center gap-2 mb-6">
                    <PieChart size={18} className="text-emerald-600" />
                    <h3 className="font-heading text-sm text-zinc-900">Captacion de leads</h3>
                  </div>
                  <div className="flex items-center justify-center gap-8">
                    <div className="relative w-40 h-40">
                      <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                        <circle cx="50" cy="50" r="40" fill="none" stroke="#f4f4f5" strokeWidth="12" />
                        <motion.circle cx="50" cy="50" r="40" fill="none" stroke="#059669" strokeWidth="12" strokeDasharray={`${2 * Math.PI * 40}`} initial={{ strokeDashoffset: 2 * Math.PI * 40 }} animate={{ strokeDashoffset: 2 * Math.PI * 40 * (1 - Math.min(((stats.leads ?? 0) / Math.max((stats.leads ?? 0) + (stats.contacts ?? 0), 1)), 1)) }} transition={{ delay: 0.6, duration: 1.2, ease: "easeOut" }} strokeLinecap="round" />
                        <motion.circle cx="50" cy="50" r="28" fill="none" stroke="#3b82f6" strokeWidth="10" strokeDasharray={`${2 * Math.PI * 28}`} initial={{ strokeDashoffset: 2 * Math.PI * 28 }} animate={{ strokeDashoffset: 2 * Math.PI * 28 * (1 - Math.min(((stats.contacts ?? 0) / Math.max((stats.leads ?? 0) + (stats.contacts ?? 0), 1)), 1)) }} transition={{ delay: 0.8, duration: 1.2, ease: "easeOut" }} strokeLinecap="round" />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="font-heading text-2xl text-zinc-900">{(stats.leads ?? 0) + (stats.contacts ?? 0)}</span>
                        <span className="text-xs text-zinc-500">Total</span>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-emerald-500" />
                        <span className="text-sm text-zinc-600">Leads: <strong>{stats.leads ?? 0}</strong></span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-blue-500" />
                        <span className="text-sm text-zinc-600">Contactos: <strong>{stats.contacts ?? 0}</strong></span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="bg-white border border-zinc-200 p-6">
                  <div className="flex items-center gap-2 mb-6"><Activity size={18} className="text-emerald-600" /><h3 className="font-heading text-sm text-zinc-900">Actividad del sitio</h3></div>
                  <div className="flex items-end gap-1 h-32">
                    {Array.from({ length: 12 }, (_, i) => {
                      const heights = [35, 52, 40, 68, 55, 80, 65, 90, 72, 85, 60, 95];
                      return (<motion.div key={i} initial={{ height: 0 }} animate={{ height: `${heights[i]}%` }} transition={{ delay: 0.6 + i * 0.06, duration: 0.5, ease: "easeOut" }} className="flex-1 bg-emerald-500/80 hover:bg-emerald-600 transition-colors rounded-t cursor-pointer" title={`Mes ${i + 1}`} />);
                    })}
                  </div>
                  <div className="flex justify-between mt-2 text-xs text-zinc-400"><span>Ene</span><span>Jun</span><span>Dic</span></div>
                </motion.div>

                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }} className="bg-white border border-zinc-200 p-6">
                  <div className="flex items-center gap-2 mb-6"><TrendingUp size={18} className="text-emerald-600" /><h3 className="font-heading text-sm text-zinc-900">Embudo de conversion</h3></div>
                  <div className="space-y-3">
                    {[
                      { label: "Visitantes", value: 1200, width: 100, color: "bg-zinc-300" },
                      { label: "Interesados", value: 340, width: 75, color: "bg-blue-400" },
                      { label: "Leads capturados", value: stats.leads ?? 0, width: 45, color: "bg-amber-400" },
                      { label: "Contactos directos", value: stats.contacts ?? 0, width: 25, color: "bg-emerald-500" },
                    ].map((step, i) => (
                      <motion.div key={i} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.7 + i * 0.12 }} className="flex items-center gap-3">
                        <motion.div initial={{ width: 0 }} animate={{ width: `${step.width}%` }} transition={{ delay: 0.8 + i * 0.12, duration: 0.6, ease: "easeOut" }} className={`h-8 ${step.color} flex items-center px-3`}>
                          <span className="text-xs font-bold text-white truncate">{step.value}</span>
                        </motion.div>
                        <span className="text-xs text-zinc-500 whitespace-nowrap">{step.label}</span>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>

                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }} className="bg-white border border-zinc-200 p-6">
                  <div className="flex items-center gap-2 mb-6"><PieChart size={18} className="text-emerald-600" /><h3 className="font-heading text-sm text-zinc-900">Cobertura por seccion</h3></div>
                  <div className="space-y-3">
                    {[
                      { label: "Soluciones", pct: Math.min(((stats.solutions ?? 0) / 9) * 100, 100) },
                      { label: "Sistemas", pct: Math.min(((stats.systems ?? 0) / 4) * 100, 100) },
                      { label: "Fichas", pct: Math.min(((stats.technical_sheets ?? 0) / 17) * 100, 100) },
                      { label: "Proyectos", pct: Math.min(((stats.projects ?? 0) / 5) * 100, 100) },
                    ].map((item, i) => (
                      <div key={i}>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-zinc-600">{item.label}</span>
                          <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 + i * 0.1 }} className="font-bold text-emerald-600">{Math.round(item.pct)}%</motion.span>
                        </div>
                        <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
                          <motion.div initial={{ width: 0 }} animate={{ width: `${item.pct}%` }} transition={{ delay: 0.9 + i * 0.1, duration: 0.7, ease: "easeOut" }} className="h-full bg-emerald-500 rounded-full" />
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </div>
            </div>
          ) : activeTab === "home" ? (
            <HomeSettings />
          ) : activeTab === "instagram" ? (
            <InstagramSettings />
          ) : (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h1 className="font-heading text-2xl text-zinc-900">
                  {tabs.find(t => t.id === activeTab)?.label}
                </h1>
                {canCreate && (
                  <Button onClick={handleCreate} data-testid="admin-create-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-none text-sm font-bold">
                    <Plus size={16} className="mr-1" /> NUEVO
                  </Button>
                )}
              </div>
              <AdminTable
                items={data}
                columns={config.columns || []}
                onEdit={canCreate ? handleEdit : undefined}
                onDelete={canCreate ? handleDelete : undefined}
              />
            </div>
          )}
        </div>
      </main>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl rounded-none max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="font-heading">{editItem ? "Editar" : "Crear"} {tabs.find(t => t.id === activeTab)?.label}</DialogTitle>
          </DialogHeader>
          {activeTab === "solutions" ? (
            <SolutionForm item={editItem} onSubmit={handleSubmit} onCancel={() => setDialogOpen(false)} />
          ) : activeTab === "systems" ? (
            <SystemForm item={editItem} onSubmit={handleSubmit} onCancel={() => setDialogOpen(false)} />
          ) : activeTab === "projects" ? (
            <ProjectForm item={editItem} onSubmit={handleSubmit} onCancel={() => setDialogOpen(false)} />
          ) : config.fields?.length > 0 ? (
            <GenericForm fields={config.fields} item={editItem} onSubmit={handleSubmit} onCancel={() => setDialogOpen(false)} />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
